'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'
import React from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxShare } from '@/components/01-atoms/Share/Share'
import { linkUrl } from '@/lib/link'
import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'
import { isAvailableForLocation, isResolvedStory, resolveStoryRelation } from '@/utils/story'

import { PhnxBusinessstory } from './Businessstory'
import { BusinessstoryProps } from './Businessstory'

interface BusinessstoryBlokProps {
  blok: {
    location: BusinessstoryProps['location']
    title: BusinessstoryProps['title']
    content: SbBlokData[]
    video: SbBlokData
    cta?: SbBlokData[]
    author: string
    relatedArticles?: string[]
  } & SbBlokData
  rels?: any
  name: string
  siteSettings?: any
  uuid: string
}

interface Story {
  name: string | number | boolean | object | undefined
  uuid: string
  content: {
    card_asset: { filename: string; alt: string; focus: string }
    title: string
    cardtitle?: string
    location: string
    content: Array<{ component: string; content?: Array<{ content?: Array<{ text?: string }> }> }>
  }
  full_slug: string
}

interface Article {
  image: { src: string; alt: string; focus: string }
  title: string
  place: string
  description: string
  link: string
}

const Businessstory: FC<BusinessstoryBlokProps> = ({ blok, rels, siteSettings, name }) => {
  const [relatedArticlesFind, setRelatedArticles] = useState<any[]>([])
  const authorRel = resolveStoryRelation(blok.author, rels) ?? blok.author
  const subscribeURL = linkUrl(siteSettings?.subscribe_link)

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      let articlesToShow: Article[] = []

      if (blok.relatedArticles && Array.isArray(blok.relatedArticles) && rels) {
        // Since relatedArticles contains full story objects, we can use them directly
        const relatedArticlesFind = blok.relatedArticles
          .filter((story: any) => {
            // Skip if the story is the current one (using uuid as identifier)
            return story.uuid !== blok.uuid
          })
          .map((story: any) => {
            let description = ''
            const contentArray = story.content.content

            const contentArrayHasText = contentArray?.find(
              (item: { component: string }) => item.component === 'rich_text'
            )

            if (contentArrayHasText?.content?.[0]?.content?.[0]?.text) {
              description = contentArrayHasText.content[0].content[0].text
            }

            return {
              image: {
                src: story.content.card_asset.filename,
                alt: story.content.card_asset.alt,
                focus: story.content.card_asset.focus,
              },
              title:
                story.content.cardtitle && story.content.cardtitle !== ''
                  ? story.content.cardtitle
                  : story.content.title,
              place: story.content.location,
              description: description,
              link: `/${story.full_slug}`,
            }
          })

        articlesToShow = articlesToShow.concat(relatedArticlesFind)

        // If we need more articles, fetch additional ones
        if (relatedArticlesFind.length < 3) {
          const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
          const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

          const sbParams = {
            ...searchParamsData,
            filter_query: {
              component: { in: 'business_story' },
              __or: [
                { 'settings.location_available': { in: location } },
                { 'settings.location_available': { not_in: filterLocation } },
              ],
            },
            sort_by: 'created_at:desc',
            per_page: 6,
          }

          const storyblokApi = getStoryblokApi()
          const { data } = await storyblokApi.get('cdn/stories/', sbParams)

          if (data?.stories) {
            // Map fetched articles to the desired format
            const fetchedArticlesArray = data.stories
              .filter((story: Story) => story.name !== blok.name)
              .map((story: Story) => {
                let description = ''
                const contentArray = story.content.content
                if (contentArray[0]?.content?.[0]?.content?.[0]?.text) {
                  description = contentArray[0].content[0].content[0].text
                }

                return {
                  image: {
                    src: story.content.card_asset.filename,
                    alt: story.content.card_asset.alt,
                    focus: story.content.card_asset.focus,
                  },
                  title:
                    story.content.cardtitle && story.content.cardtitle !== ''
                      ? story.content.cardtitle
                      : story.content.title,
                  place: story.content.location,
                  description: description,
                  link: `/${story.full_slug}`,
                }
              })

            articlesToShow = articlesToShow.concat(fetchedArticlesArray)
          }
        }
      }

      // Remove duplicates based on the link property
      const uniqueArticles = Array.from(
        new Map(articlesToShow.map((article) => [article.link, article])).values()
      )

      // Limit to 3 articles
      const limitedArticles = uniqueArticles.slice(0, 3)

      setRelatedArticles(limitedArticles)
    }

    fetchRelatedArticles()
  }, [blok._uid, blok.relatedArticles, rels, blok.name])

  return (
    <div {...storyblokEditable(blok)}>
      <PhnxBusinessstory
        title={blok.title}
        h1={name}
        location={blok.location}
        description={
          blok.content &&
          blok.content.map((child: SbBlokData) => (
            <React.Fragment key={child._uid}>
              <StoryblokServerComponent blok={child} key={child._uid} />
              {child.component === 'video_holder' && (
                <div
                  style={{ width: `${child.setWidth}%` }}
                  className={`flex justify-center ${child.layout === 'center' ? 'mx-auto' : ''}`}
                >
                  <PhnxShare title={blok.title} facebook linkedin email className="items-end" />
                </div>
              )}
            </React.Fragment>
          ))
        }
        author={
          isResolvedStory(authorRel)
            ? {
                name: authorRel.name || '',
                image: {
                  src: authorRel.content?.image?.filename || '',
                  alt: authorRel.content?.image?.alt || '',
                },
                bio: authorRel.content?.bio?.[0]?.content?.content?.[0]?.content?.[0]?.text || '',
                title: authorRel.content?.title || '',
                // Only link to the profile when the adviser page exists on this
                // site, an NZ-only adviser 404s on the AU build and vice versa.
                url: isAvailableForLocation(
                  authorRel.content?.settings?.[0]?.location_available,
                  process.env.NEXT_PUBLIC_NEXT_LOCATION || 'default'
                )
                  ? `/${authorRel.full_slug}`
                  : undefined,
              }
            : undefined
        }
        video={
          blok.video && Array.isArray(blok.video)
            ? blok.video.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null
        }
        cta={
          blok.cta && Array.isArray(blok.cta)
            ? blok.cta.map((child: SbBlokData) => (
                <StoryblokServerComponent blok={child} key={child._uid} />
              ))
            : null
        }
        subscribe={{
          title: 'Subscribe to receive the next chapter',
          button: <PhnxButton type="link" href={subscribeURL} label="Subscribe Now" />,
        }}
        relatedArticles={{
          title: "Mystery novels we've helped turn into masterpieces",
          items: relatedArticlesFind,
        }}
      />
    </div>
  )
}

export default Businessstory
